'use client'

import cx from 'classnames'
import { FC, ReactNode, useCallback, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'

import '@/components/Heading/Heading.scss'

type Props = {
  children?: ReactNode
  rightChildren?: ReactNode
  fontSize?: string
  headingType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  fontFamily?: 'YuGothic' | 'YuMincho' | 'Goldenbook' | 'neue'
  isLine?: boolean
  className?: string
}

const Heading: FC<Props> = ({
  children,
  fontSize,
  headingType = 'h1',
  fontFamily = 'YuMincho',
  isLine = false,
  className,
}) => {
  const { inView, ref } = useInView({
    threshold: 0,
    rootMargin: '0px 0px -20% 0px',
    triggerOnce: true,
  })

  const fontFamilyClass = useCallback(() => {
    switch (fontFamily) {
      case 'YuGothic':
        return 'u-font-yu-go'
      case 'YuMincho':
        return 'u-font-yu-min'
      case 'neue':
        return 'u-font-neue'
      case 'Goldenbook':
        return 'u-font-gb'
    }
  }, [fontFamily])

  const style = useMemo(() => {
    return {
      fontSize: fontSize,
    }
  }, [fontSize])

  switch (headingType) {
    case 'h1':
      return (
        <h1
          ref={ref}
          className={cx(`c-heading c-heading--h1 ${fontFamilyClass()}`, {
            'c-heading__inview': inView,
            'c-heading--line': isLine,
            [className as string]: className,
          })}
          style={style}
        >
          {children}
        </h1>
      )
    case 'h2':
      return (
        <h2
          className={cx(`c-heading c-heading--h2 ${fontFamilyClass()}`, {
            'c-heading--line': isLine,
            [className as string]: className,
          })}
          style={style}
        >
          {children}
        </h2>
      )
    case 'h3':
      return (
        <h3
          className={cx(`c-heading c-heading--h3 ${fontFamilyClass()}`, {
            'c-heading--line': isLine,
            [className as string]: className,
          })}
          style={style}
        >
          {children}
        </h3>
      )
    case 'h4':
      return (
        <h4
          className={cx(`c-heading c-heading--h4 ${fontFamilyClass()}`, {
            'c-heading--line': isLine,
            [className as string]: className,
          })}
          style={style}
        >
          {children}
        </h4>
      )
    case 'h5':
      return (
        <h5
          className={cx(`c-heading c-heading--h5 ${fontFamilyClass()}`, {
            'c-heading--line': isLine,
            [className as string]: className,
          })}
          style={style}
        >
          {children}
        </h5>
      )
    case 'h6':
      return (
        <h6
          className={cx(`c-heading c-heading--h6 ${fontFamilyClass()}`, {
            'c-heading--line': isLine,
            [className as string]: className,
          })}
          style={style}
        >
          {children}
        </h6>
      )
  }
}

export default Heading
